/**
 * The SVG DOCUMENT MODEL (ADR 0009): pure data describing the rendered
 * section. packages/environment renders (EnvironmentDefinition,
 * EnvironmentView, packs) into this model; apps/web owns the React wrapper
 * that maps it to elements, and the prologue build serializes it to a
 * string. No React here, no DOM types, no I/O.
 *
 * Every visual property is a PRESENTATION ATTRIBUTE in `attrs` — line
 * weights, currentColor strokes, opacity. Document CSS cannot cross the
 * <use>/<symbol> shadow boundary (the prototype's first build rendered
 * every symbol as a solid black blob for exactly this reason), so the model
 * never assumes a stylesheet exists.
 */
export function el(tag, attrs = {}, children = [], text) {
    return text === undefined ? { tag, attrs, children } : { tag, attrs, children, text };
}
const ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" };
function escapeXml(value) {
    return value.replace(/[&<>"]/g, (c) => ESCAPES[c] ?? c);
}
function serializeNode(node) {
    const attrs = Object.entries(node.attrs)
        .map(([k, v]) => ` ${k}="${escapeXml(v)}"`)
        .join("");
    const inner = (node.raw ?? "") + (node.text !== undefined ? escapeXml(node.text) : "") + node.children.map(serializeNode).join("");
    return inner.length === 0 ? `<${node.tag}${attrs}/>` : `<${node.tag}${attrs}>${inner}</${node.tag}>`;
}
/**
 * Serializes the model to a standalone <svg> string — used by the prologue
 * build (static heroes, zero requests) and by tests that scan the rendered
 * output for leaks. The client React wrapper does NOT use this; it maps the
 * model to elements directly.
 */
export function toSvgString(doc, options) {
    const { x, y, w, h } = doc.viewBox;
    const idAttr = options?.id === undefined ? "" : ` id="${escapeXml(options.id)}"`;
    const revealAttr = doc.reveal === null
        ? ""
        : ` data-crop-y="${String(doc.reveal.cropY)}" data-crop-h="${String(doc.reveal.cropH)}" data-reveal-min="${String(doc.reveal.minY)}" data-reveal-max="${String(doc.reveal.maxY)}"`;
    return (`<svg${idAttr}${revealAttr} xmlns="http://www.w3.org/2000/svg" viewBox="${x} ${y} ${w} ${h}" role="img" aria-label="${escapeXml(doc.description)}">` +
        `<title>${escapeXml(doc.label)}</title>` +
        `<defs>${doc.defs.map(serializeNode).join("")}</defs>` +
        doc.body.map(serializeNode).join("") +
        `</svg>`);
}
//# sourceMappingURL=svg-model.js.map